import React, { useState, useEffect, useMemo, useRef } from 'react';
import { testimonials2 } from '../../constants/content';
import type { Testimonial } from '../../types';

// ── Constants ────────────────────────────────────────────────────────────────

const TARGET_DATE = new Date('2026-08-07T22:00:00.000Z');

const WHATSAPP_URL = `https://wa.me/59898816513?text=${encodeURIComponent(
  '¡Hola Marha! Quiero inscribirme al Taller de Manejo del Dolor en el Parto del 7 de Agosto. ¿Podés darme más info?'
)}`;

const WA_ICON = (
    <svg className="w-12 h-12 text-green-500 shrink-0" fill="currentColor" viewBox="0 0 24 24">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
      <path d="M12 0C5.373 0 0 5.373 0 12c0 2.125.558 4.122 1.532 5.856L.057 23.213a.75.75 0 0 0 .916.916l5.355-1.475A11.952 11.952 0 0 0 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-1.907 0-3.694-.528-5.22-1.446l-.374-.223-3.878 1.068 1.068-3.878-.223-.374A9.96 9.96 0 0 1 2 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z" />
    </svg>
);

// ── Hooks ────────────────────────────────────────────────────────────────────

function useCountdown(target: Date) {
  const calc = () => Math.max(0, target.getTime() - Date.now());
  const [ms, setMs] = useState(calc);

  useEffect(() => {
    if (ms <= 0) return;
    const id = setInterval(() => setMs(calc), 1000);
    return () => clearInterval(id);
  });

  return {
    days: Math.floor(ms / 86_400_000),
    hours: Math.floor((ms % 86_400_000) / 3_600_000),
    minutes: Math.floor((ms % 3_600_000) / 60_000),
    seconds: Math.floor((ms % 60_000) / 1_000),
    expired: ms <= 0,
  };
}

function useScrollReveal() {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -48px 0px' }
    );
    document.querySelectorAll('.fade-in-up').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);
}

// ── Sub-components ───────────────────────────────────────────────────────────

const CountdownUnit: React.FC<{ value: number; label: string; dark?: boolean }> = ({
  value,
  label,
  dark = false,
}) => (
  <div className="flex flex-col items-center">
    <div
      className={`text-2xl md:text-3xl font-bold font-mono rounded-xl px-3 py-2 min-w-[56px] text-center tabular-nums ${
        dark
          ? 'bg-white/15 text-white backdrop-blur-sm border border-white/20'
          : 'bg-contessa-600 text-white'
      }`}
    >
      {String(value).padStart(2, '0')}
    </div>
    <span
      className={`text-[10px] uppercase tracking-widest mt-1 font-semibold ${
        dark ? 'text-contessa-200' : 'text-contessa-500'
      }`}
    >
      {label}
    </span>
  </div>
);

const SparkleOverlay: React.FC = () => {
  const sparks = useMemo(
    () =>
      Array.from({ length: 35 }).map((_, i) => ({
        id: i,
        top: Math.random() * 100,
        left: Math.random() * 100,
        size: Math.random() * 2 + 1,
        delay: Math.random() * -6,
        duration: Math.random() * 3 + 2,
        opacity: 0.3 + Math.random() * 0.6,
      })),
    []
  );
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden" style={{ zIndex: 0 }}>
      {sparks.map((s) => (
        <span
          key={s.id}
          className="dot-spark"
          style={{
            top: `${s.top}%`,
            left: `${s.left}%`,
            width: `${s.size * 3}px`,
            height: `${s.size * 3}px`,
            opacity: s.opacity,
            animationDelay: `${s.delay}s`,
            animationDuration: `${s.duration}s`,
          }}
        />
      ))}
    </div>
  );
};

const CTAButton: React.FC<{ large?: boolean }> = ({ large = false }) => (
  <a
    href={WHATSAPP_URL}
    target="_blank"
    rel="noopener noreferrer"
    className={`vsl-cta-pulse inline-flex items-center gap-6 bg-white text-contessa-700 hover:bg-contessa-50 font-bold rounded-full shadow-2xl transition-all duration-300 hover:-translate-y-1 ${
      large ? 'text-xl px-12 py-5' : 'text-lg px-8 py-4'
    }`}
  >
    {WA_ICON}
    {large ? 'Quiero inscribirme ahora' : <>Quiero inscribirme<br />$2000 / U$S50</>}
  </a>
);

const FAQItem: React.FC<{ question: string; answer: string }> = ({ question, answer }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-contessa-200 rounded-2xl overflow-hidden mb-3 shadow-sm">
      <button
        className="w-full flex items-center justify-between px-6 py-4 text-left bg-white hover:bg-contessa-50 transition-colors duration-200"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className="font-semibold text-contessa-800 text-sm md:text-base pr-4">{question}</span>
        <span
          className={`text-contessa-400 text-2xl font-light shrink-0 transition-transform duration-300 ${
            open ? 'rotate-45' : ''
          }`}
        >
          +
        </span>
      </button>
      <div
        className={`overflow-hidden transition-all duration-300 ${open ? 'max-h-80' : 'max-h-0'}`}
      >
        <p className="px-6 py-4 text-contessa-600 text-sm leading-relaxed bg-contessa-50/70">
          {answer}
        </p>
      </div>
    </div>
  );
};

const TestimonialCarousel: React.FC<{ items: Array<Testimonial & { content: string }> }> = ({ items }) => {
  const [current, setCurrent] = useState(0);
  const [fading, setFading] = useState(false);
  const [paused, setPaused] = useState(false);
  const [visible, setVisible] = useState(false);
  const touchStartX = useRef<number | null>(null);
  const resumeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Option B: pause when carousel is not in the viewport
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => setVisible(entry.isIntersecting),
      { threshold: 0.3 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Auto-rotation: only runs when visible AND not paused
  useEffect(() => {
    if (!visible || paused) return;
    const id = setInterval(() => {
      setFading(true);
      setTimeout(() => {
        setCurrent((c) => (c + 1) % items.length);
        setFading(false);
      }, 350);
    }, 4500);
    return () => clearInterval(id);
  }, [visible, paused, items.length]);

  // Cleanup resume timer on unmount
  useEffect(() => () => { if (resumeTimer.current) clearTimeout(resumeTimer.current); }, []);

  const scheduleResume = (delay: number) => {
    if (resumeTimer.current) clearTimeout(resumeTimer.current);
    resumeTimer.current = setTimeout(() => setPaused(false), delay);
  };

  const goTo = (i: number) => {
    if (i === current) return;
    setFading(true);
    setTimeout(() => {
      setCurrent(i);
      setFading(false);
    }, 350);
  };

  // Option A: pause on hover (desktop)
  const handleMouseEnter = () => {
    if (resumeTimer.current) clearTimeout(resumeTimer.current);
    setPaused(true);
  };
  const handleMouseLeave = () => scheduleResume(2000);

  // Option A: pause when user picks a dot manually
  const handleDotClick = (i: number) => {
    setPaused(true);
    scheduleResume(6000);
    goTo(i);
  };

  // Option A: pause on touch (mobile)
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    if (resumeTimer.current) clearTimeout(resumeTimer.current);
    setPaused(true);
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(delta) >= 40) {
      goTo(delta < 0 ? (current + 1) % items.length : (current - 1 + items.length) % items.length);
    }
    scheduleResume(6000);
  };

  const t = items[current];

  return (
    <div ref={containerRef}>
      {/* Card */}
      <div
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        style={{
          opacity: fading ? 0 : 1,
          transform: fading ? 'translateY(14px)' : 'translateY(0)',
          transition: 'opacity 350ms ease, transform 350ms ease',
        }}
        className="bg-contessa-50 border border-contessa-100 rounded-3xl px-8 pt-6 pb-8 shadow-md cursor-grab active:cursor-grabbing"
      >
        <span className="text-contessa-200 text-6xl font-serif leading-none select-none block mb-1">&ldquo;</span>
        <p className="text-contessa-600 text-base leading-relaxed mb-7 -mt-2">{t.content}</p>
        <div className="flex items-center gap-3">
          <div>
            <p className="font-bold text-contessa-800 text-sm">{t.name}</p>
          </div>
        </div>
      </div>

      {/* Dot indicators */}
      <div className="flex justify-center items-center gap-2 mt-6">
        {items.map((_, i) => (
          <button
            key={i}
            onClick={() => handleDotClick(i)}
            aria-label={`Testimonio ${i + 1}`}
            className={`rounded-full transition-all duration-300 ${
              i === current
                ? 'w-7 h-2.5 bg-contessa-500'
                : 'w-2.5 h-2.5 bg-contessa-200 hover:bg-contessa-400'
            }`}
          />
        ))}
      </div>
    </div>
  );
};

// ── Main page ────────────────────────────────────────────────────────────────

const TallerDolorParto: React.FC = () => {
  const { days, hours, minutes, seconds } = useCountdown(TARGET_DATE);
  useScrollReveal();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    document.title = 'Taller de Manejo del Dolor en el Parto · Marha Scanu';
    return () => {
      document.title = 'Los ciclos de Marha';
    };
  }, []);

  const textTestimonials = testimonials2
    .filter((t): t is Testimonial & { content: string } => !t.youtubeId && Boolean(t.content));

  return (
    <div className="min-h-screen bg-contessa-50 font-sans overflow-x-hidden">
      {/* ── A. HERO ────────────────────────────────────────────────────────── */}
      <section className="relative bg-gradient-to-br from-contessa-950 via-contessa-800 to-contessa-600 text-white overflow-hidden">
        <SparkleOverlay />
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: `radial-gradient(circle, rgba(255,255,255,0.06) 1px, transparent 1px)`,
            backgroundSize: '38px 38px',
            zIndex: 0,
          }}
        />

        <div className="relative z-10 container mx-auto px-5 md:px-8 pt-12 pb-0">
          {/* Countdown pill */}
          <div className="flex justify-center mb-8">
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl px-5 py-3 flex flex-wrap items-center gap-3 shadow-xl">
              <span className="text-xs uppercase tracking-widest text-contessa-200 font-semibold hidden sm:block">
                Comienza en
              </span>
              <div className="flex items-center gap-2">
                <CountdownUnit value={days} label="días" dark />
                <span className="text-white/50 font-bold text-xl">:</span>
                <CountdownUnit value={hours} label="hs" dark />
                <span className="text-white/50 font-bold text-xl">:</span>
                <CountdownUnit value={minutes} label="min" dark />
                <span className="text-white/50 font-bold text-xl">:</span>
                <CountdownUnit value={seconds} label="seg" dark />
              </div>
            </div>
          </div>

          <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16 pb-6">
            {/* ─ Text ─ */}
            <div className="flex-1 text-center lg:text-left">
              <div className="inline-flex items-center gap-2 bg-white/15 border border-white/20 rounded-full px-4 py-1.5 text-xs uppercase tracking-widest font-semibold text-contessa-100 mb-6">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                7 de Agosto · 19hs - Uruguay
              </div>

              <p className="text-lg md:text-xl text-contessa-100 mb-3 max-w-xl mx-auto lg:mx-0 leading-relaxed">
                Taller de manejo del dolor en el parto
              </p>

              <h1 className="text-3xl md:text-5xl xl:text-6xl font-serif font-bold leading-tight mb-5">
                <span className="relative inline-block">
                  <span className="vsl-shimmer">Conectar con tu cuerpo</span>
                  <span className="absolute -bottom-1 left-0 w-full h-0.5 bg-contessa-300/60 rounded-full" />
                </span>
              </h1>


              <p className="text-sm text-contessa-300 mb-8 max-w-xl mx-auto lg:mx-0 italic">
                Herramientas reales para atravesar el dolor con calma, confianza y presencia
              </p>

              <CTAButton />
              <p className="text-xs text-contessa-300 mt-3">
                Precio válido hasta el 7 de Agosto · Cupos limitados
              </p>
            </div>

            {/* ─ Image ─ */}
            <div className="shrink-0 w-56 md:w-72 lg:w-88 relative">
              <div className="absolute inset-0 rounded-3xl bg-contessa-400/25 blur-3xl scale-110" />
              <img
                src="/images/hero-1.jpg"
                alt="Manejo del dolor en el parto"
                className="relative z-10 rounded-3xl shadow-2xl w-full object-cover animate-float"
                style={{ aspectRatio: '4/5' }}
                loading="eager"
              />
            </div>
          </div>
        </div>

        {/* wave divider */}
        <div className="relative z-10 leading-none" style={{ marginBottom: '-2px' }}>
          <svg
            viewBox="0 0 1440 56"
            className="w-full block"
            preserveAspectRatio="none"
            style={{ height: '48px' }}
          >
            <path fill="#fdf8f7" d="M0,28 C360,56 1080,0 1440,28 L1440,56 L0,56 Z" />
          </svg>
        </div>
      </section>

      {/* ── B. IDENTIFICATION ─────────────────────────────────────────────── */}
      <section className="py-16" style={{ backgroundColor: '#fdf8f7' }}>
        <div className="container mx-auto px-5 md:px-8 max-w-3xl text-center">
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-contessa-900 mb-10 fade-in-up">
            ¿Qué sentis cuando pensas en el parto?
          </h2>

          <div className="grid md:grid-cols-2 gap-4 text-center">
            {[
              { text: 'Tengo miedo al dolor de las contracciones' },
              { text: 'No sé qué hacer con el dolor' },
              { text: 'Mi pareja no tiene herramientas' },
              { text: 'Quiero conocer más mi cuerpo' },
              { text: 'Quiero vivir una experiencia amorosa' },
            ].map((item, i) => (
              <div
                key={i}
                className="fade-in-up flex items-start gap-4 bg-white border border-contessa-100 rounded-2xl px-5 py-4 shadow-sm"
                style={{ transitionDelay: `${i * 75}ms` }}
              >
                <p className="text-contessa-700 text-base leading-relaxed">{item.text}</p>
              </div>
            ))}
          </div>

          <p className="text-contessa-600 mt-10 text-lg md:text-xl font-serif italic fade-in-up">
            Si sentís una o más <br />{' '}
            <span className="text-contessa-800 font-bold not-italic">
              ¡llegaste al lugar correcto!
            </span>
          </p>
        </div>
      </section>

      {/* ── F. MINI-TRANSFORMATIONS ──────────────────────────────────────── */}
      <section className="pt-16 pb-4 bg-white">
        <div className="container mx-auto px-5 md:px-8 max-w-3xl">
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-contessa-900 mb-10 text-center fade-in-up">
            ¿De qué se trata el taller?
          </h2>
          <div className="space-y-4">
            {[
              {
                icon: '1',
                title: 'Entender el cuerpo y el proceso fisiológico',
                text: 'Van a aprender a reconocer las señales físicas reales de que el parto se acerca y a distinguir qué situaciones ameritan una consulta médica de emergencia.',
              },
              {
                icon: '2',
                title: 'Herramientas prácticas de alivio y el rol del acompañante',
                text: 'Van a practicar recursos concretos de alivio inmediato como técnicas de respiración guiada, uso del movimiento, cambios de posición y anclajes en el presente. Además, el acompañante aprende herramientas específicas para ejercer un liderazgo compasivo, ofreciendo el sostén físico y emocional que la madre necesita en los momentos de crisis.',
              },
              {
                icon: '3',
                title: 'Decisiones informadas y derechos en el nacimiento',
                text: 'Van a aprender sobre las distintas formas de nacer y el uso de la anestesia epidural, ayudando a discernir si su uso responde a un deseo genuino o a la necesidad de mayor soporte. Todo esto bajo el marco de los derechos legales de la familia en Uruguay, asegurando que participen como los verdaderos protagonistas de su propio proceso.',
              }
            ].map((item, i) => (
              <div
                key={i}
                className="fade-in-up flex items-center gap-5 bg-white border border-contessa-100 rounded-2xl px-6 py-5 shadow-sm"
                style={{ transitionDelay: `${i * 85}ms` }}
              >
                <span className="text-3xl font-bold shrink-0 w-12 h-12 flex items-center justify-center rounded-full bg-contessa-500 text-white">{item.icon}</span>
                <div>
                  <h4 className="font-bold text-contessa-800 mb-1">{item.title}</h4>
                  <p className="text-contessa-600 text-sm leading-relaxed">{item.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── D. BONOS INCLUIDOS ────────────────────────────────────────────── */}
      <section className="pt-4 pb-16 bg-white">
        <div className="container mx-auto px-5 md:px-8 max-w-3xl">
          <h2 className="text-2xl md:text-3xl font-serif font-bold text-contessa-900 mb-10 text-center fade-in-up">
            Bonos Incluidos:
          </h2>
          <div className="space-y-4">
            {[
              {
                icon: '📋',
                title: 'Manual de consulta en emergencias',
                text: 'Aprende qué observar en tu cuerpo y qué hacer en cada caso: esperar, monitorear o consultar. Para no entrar en pánico ni depender de buscar en internet en ese momento.',
              },
              {
                icon: '🧘',
                title: 'Mini clase grabada de posturas de alivio',
                text: 'Basado en el Método embarazo y nacimiento eutónico. Aprenderás técnicas comprobadas para encontrar alivio en cada etapa del parto.',
              },
              {
                icon: '💑',
                title: 'Guía PDF de acuerdos con la pareja y familia',
                text: 'Para que tu entorno sea apoyo real y no una fuente de tensión. Incluye qué necesita saber tu pareja y cómo acompañarte en cada momento del parto.',
              },
              {
                icon: '▶️',
                title: 'Acceso a la clase grabada al día siguiente',
                text: 'Vas a poder verla y practicar las veces que necesites. Sin límite de reproducciones, para que repases cuando sientas que lo necesitas.',
              }
            ].map((item, i) => (
              <div
                key={i}
                className="fade-in-up flex items-start gap-4 bg-gradient-to-r from-contessa-50 to-white border border-contessa-100 rounded-2xl px-6 py-5 shadow-sm"
                style={{ transitionDelay: `${i * 75}ms` }}
              >
                <span className="text-2xl shrink-0 mt-1">{item.icon}</span>
                <div className="flex-1">
                  <h4 className="font-bold text-contessa-800 mb-2">{item.title}</h4>
                  <p className="text-contessa-600 text-sm leading-relaxed">{item.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── E. POSSIBILITY — Marha's story ───────────────────────────────── */}
      <section className="py-16 bg-contessa-50">
        <div className="container mx-auto px-5 md:px-8 max-w-4xl">
          <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-14">
            <div className="shrink-0 w-44 md:w-52 fade-in-up">
              <img
                src="/images/about-me2.avif"
                alt="Marha Scanu"
                className="rounded-3xl shadow-xl w-full object-cover"
                style={{ aspectRatio: '3/4' }}
                loading="lazy"
              />
            </div>
            <div className="fade-in-up" style={{ transitionDelay: '110ms' }}>
              <p className="text-contessa-400 uppercase tracking-widest text-xs font-semibold mb-3">
                Quién te acompaña
              </p>
              <h2 className="text-2xl md:text-3xl font-serif font-bold text-contessa-900 mb-4">
                Soy <span className="text-contessa-500">Marha Scanu</span>, acompaño a familias 
                como vos hace más de 8 años.
              </h2>
              <div className="space-y-3 text-contessa-600 text-sm leading-relaxed">
                <p>
                  Creé este taller porque se de la importancia de vivir el nacimiento de nuestros 
                  hijos e hijas de forma amorosa y en calma. Mi objetivo es que en dos horas puedas 
                  comprender el dolor desde otro lugar, y crear las herramientas que te acompañen ese día. 
                  También que tu pareja o quien te acompaña se convierta en tu pilar, y juntos en un equipo.
                </p>
                <p>
                  Mi método se sostiene sobre tres pilares fundamentales: {' '}
                  <strong className="text-contessa-800">información</strong> (derechos, guías, 
                  saber qué esperar), <strong className="text-contessa-800">contención </strong> 
                  (salud mental, escucha, la pareja como equipo) y <strong className="text-contessa-800">
                    actividades corporales</strong> (movimiento, pujo, registro físico, manejo del dolor).
                </p>
              </div>
              <div className="mt-5 flex flex-wrap gap-2">
                {[
                  'Doula',
                  'Educadora Perinatal',
                  'Educadora en Salud Materna',
                  'Método Frida Kaplan',
                  'Duelo Perinatal',
                  'Terapia Sistémica Familiar',
                ].map((tag) => (
                  <span
                    key={tag}
                    className="bg-contessa-100 text-contessa-700 text-xs font-medium px-3 py-1 rounded-full"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── G. RESULTS ───────────────────────────────────────────────────── */}
      <section className="py-16 bg-gradient-to-br from-contessa-100 to-contessa-50">
        <div className="container mx-auto px-5 md:px-8 max-w-3xl text-center">
          <p className="text-contessa-400 uppercase tracking-widest text-xs font-semibold mb-3 fade-in-up">
            Lo que te llevás
          </p>
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-contessa-900 mb-10 fade-in-up">
            ¿Cómo te vas del taller?
          </h2>
          <div className="grid sm:grid-cols-2 gap-4 text-left">
            {[
              { label: 'Comprendiendo el dolor en todas sus dimensiones:', text: 'Aprenderás que el dolor en el parto es fisiológico (del cuerpo) pero también tiene una dimensión emocional. Entender esta diferencia es clave para transitarlo mejor.' },
              { label: 'Diferenciando dolor de sufrimiento:', text: 'Vivimos en una sociedad que nos dice que el dolor es algo malo, pero en el parto no lo es. Vas a aprender a utilizarlo como aliado.' },
              { label: 'Confiando en tu cuerpo:', text: 'Tu cuerpo sabe qué hacer. Aprenderás a escucharlo, a entender sus señales y a confiar en su sabiduría incluso cuando tu mente duda.' },
              { label: 'Con herramientas prácticas para el momento:', text: 'Técnicas concretas, posturas, respiración y estrategias mentales que podrás aplicar durante el parto para manejar el dolor de forma activa.' },
              { label: 'Con el rol de tu pareja o de quien te acompaña claro:', text: 'Ambos tendrán las herramientas y serán equipo en el momento más lindo de sus vidas.' },
            ].map((r, i) => (
              <div
                key={i}
                className="fade-in-up flex items-start gap-3 bg-white rounded-2xl px-5 py-4 shadow-sm border border-contessa-100"
                style={{ transitionDelay: `${i * 65}ms` }}
              >
                <span className="text-contessa-400 font-bold text-lg shrink-0 mt-0.5">✦</span>
                <p className="text-contessa-700 text-sm leading-relaxed">
                  <strong className="text-contessa-800">{r.label}</strong><br/> {r.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── H. INTERMEDIATE CTA ───────────────────────────────────────── */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-5 md:px-8 max-w-3xl text-center fade-in-up">
          <div className="relative inline-flex group">
            {/* Glow halo */}
            <div
              className="absolute -inset-1.5 rounded-full blur-md opacity-60 group-hover:opacity-90 transition-opacity duration-300"
              style={{ background: 'linear-gradient(135deg, #c47070, #8b3535, #c47070)' }}
              aria-hidden="true"
            />
            {/* Button face */}
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="relative inline-flex items-center justify-center gap-3 bg-gradient-to-br from-contessa-500 via-contessa-600 to-contessa-800 text-white font-bold text-lg px-10 py-5 rounded-full shadow-xl transition-all duration-200 hover:scale-[1.04] active:scale-[0.97] overflow-hidden"
            >
              {/* top shine */}
              <span
                className="absolute top-0 left-0 right-0 h-1/2 rounded-t-full pointer-events-none"
                style={{ background: 'linear-gradient(to bottom, rgba(255,255,255,0.18), transparent)' }}
                aria-hidden="true"
              />
              {WA_ICON}
              <span className="relative">Inscribirme ahora</span>
            </a>
          </div>
        </div>
      </section>

      {/* ── I. TESTIMONIALS ──────────────────────────────────────────────── */}
      {textTestimonials.length > 0 && (
        <section className="py-16 bg-white">
          <div className="container mx-auto px-5 md:px-8 max-w-2xl">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-contessa-900 mb-10 text-center fade-in-up">
              Lo que dicen las familias que ya han participado
            </h2>
            <div className="fade-in-up">
              <TestimonialCarousel items={textTestimonials} />
            </div>
          </div>
        </section>
      )}

      {/* ── J. PROGRAM DETAILS ───────────────────────────────────────────── */}
      <section className="py-16 bg-contessa-50">
        <div className="container mx-auto px-5 md:px-8 max-w-3xl">
          <p className="text-contessa-400 uppercase tracking-widest text-xs font-semibold mb-3 text-center fade-in-up">
            El taller por dentro
          </p>
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-contessa-900 mb-10 text-center fade-in-up">
            ¿Qué incluye y cómo funciona?
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              { icon: '📅', label: 'Fecha', value: '7 de Agosto, 2026' },
              { icon: '⏰', label: 'Hora', value: '19:00 hs (Uruguay)' },
              { icon: '💻', label: 'Formato', value: 'Encuentro online en vivo' },
              { icon: '⌛', label: 'Duración', value: 'Aprox. 90 minutos' },
              { icon: '🎥', label: 'Grabación', value: 'Acceso a la grabación post-taller' },
              { icon: '📋', label: 'Material', value: 'Recursos digitales de apoyo' },
            ].map((d, i) => (
              <div
                key={i}
                className="fade-in-up flex items-center gap-4 bg-white rounded-2xl px-5 py-4 border border-contessa-100 shadow-sm"
                style={{ transitionDelay: `${i * 55}ms` }}
              >
                <span className="text-2xl">{d.icon}</span>
                <div>
                  <p className="text-xs uppercase tracking-wide text-contessa-400 font-semibold">
                    {d.label}
                  </p>
                  <p className="text-contessa-800 font-medium text-sm">{d.value}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── K. PRICING ───────────────────────────────────────────────────── */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-5 md:px-8 max-w-md text-center">
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-contessa-900 mb-8 fade-in-up">
            Inversión
          </h2>
          <div className="fade-in-up bg-gradient-to-br from-contessa-700 to-contessa-900 text-white rounded-3xl px-8 py-10 shadow-2xl relative overflow-hidden">
            <SparkleOverlay />
            <div className="relative z-10">
              <div className="text-4xl font-bold font-sans tracking-tight leading-none mb-1">$2000 / U$S50</div>
              <p className="text-contessa-300 text-base mb-7"></p>
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="vsl-cta-pulse inline-flex items-center justify-center gap-3 bg-white text-contessa-700 hover:bg-contessa-50 font-bold text-lg px-8 py-4 rounded-full shadow-xl transition-all duration-300 hover:-translate-y-1 w-full"
              >
                {WA_ICON}
                Inscribirme ahora
              </a>
              <p className="text-xs tracking-wide text-contessa-400 mt-3">
                Cupos limitados
              </p>
            </div>
          </div>

          {/* Countdown mini */}
          <div className="mt-8 fade-in-up">
            <p className="text-contessa-500 text-sm mb-4">El taller comienza en...</p>
            <div className="flex items-center justify-center gap-2">
              <CountdownUnit value={days} label="días" />
              <span className="text-contessa-400 font-bold text-xl mb-4">:</span>
              <CountdownUnit value={hours} label="hs" />
              <span className="text-contessa-400 font-bold text-xl mb-4">:</span>
              <CountdownUnit value={minutes} label="min" />
              <span className="text-contessa-400 font-bold text-xl mb-4">:</span>
              <CountdownUnit value={seconds} label="seg" />
            </div>
          </div>
        </div>
      </section>

      {/* ── L. FAQ ───────────────────────────────────────────────────────── */}
      <section className="py-16 bg-contessa-50">
        <div className="container mx-auto px-5 md:px-8 max-w-2xl">
          <p className="text-contessa-400 uppercase tracking-widest text-xs font-semibold mb-3 text-center fade-in-up">
            Preguntas frecuentes
          </p>
          <h2 className="text-3xl font-serif font-bold text-contessa-900 mb-10 text-center fade-in-up">
            Todo lo que necesitás saber
          </h2>
          <div className="fade-in-up">
            <FAQItem
              question="¿Este taller es solo para personas que van a parir sin epidural?"
              answer="No. Este taller es para todas las personas gestantes, independientemente del tipo de parto que deseen o planifiquen. Comprender el dolor y contar con herramientas para atravesarlo es valioso tanto si elegís utilizar analgesia como si no. La información y la preparación siempre suman."
            />
            <FAQItem
              question="¿Puedo hacerlo con mi pareja?"
              answer="¡Sí! Tu inscripción incluye la participación de tu pareja o acompañante de elección. De hecho, recomiendo que puedan hacerlo juntos, ya que muchas de las herramientas que veremos pueden ponerse en práctica en equipo y ayudan a que ambos lleguen al nacimiento con más confianza y tranquilidad."
            />
            <FAQItem
              question="¿Puedo participar aunque esté de pocas semanas de embarazo?"
              answer="¡Sí! Al  siguiente día del taller te envío la grabación, por lo que podrás verlo cuando quieras y repetirlo las veces que necesites hasta el nacimiento de tu bebé. Conocer estas herramientas con tiempo te permitirá practicarlas durante el embarazo y llegar al nacimiento con más confianza y tranquilidad."
            />
            <FAQItem
              question="¿Puedo participar si ya estoy en el tercer trimestre?"
              answer="¡Claro! Nunca es tarde para prepararte. En el taller trabajamos posturas, movimientos y herramientas que podrás empezar a aplicar de inmediato. Además, recibirás una guía sobre los signos de inicio del trabajo de parto para ayudarte a reconocer cuándo se acerca el momento y cuándo aún podés permanecer tranquila en tu hogar."
            />
            <FAQItem
              question="¿Qué pasa si no puedo asistir en vivo?"
              answer="No hay problema. Al día siguiente de la clase en vivo, tendrás acceso a la grabación completa para verla cuando puedas y tantas veces como necesites hasta el nacimiento de tu bebé."
            />
            <FAQItem
              question="¿El taller es solo información o hay parte práctica también?"
              answer="El taller combina información actualizada con trabajo corporal práctico. Vamos a explorar respiraciones, posiciones y movimientos que podés practicar desde tu casa."
            />
            <FAQItem
              question="¿Cómo me inscribo?"
              answer="Solo tenés que hacer clic en cualquiera de los botones de WhatsApp de esta página y escribirme. Coordinamos el pago y te envío toda la información y los accesos por mail o celular. En pocos minutos quedará todo pronto para que puedas comenzar con los bonus y agendar la fecha del taller en vivo."
            />            
          </div>
        </div>
      </section>
    </div>
  );
};

export default TallerDolorParto;
